import { ClientType, IClientFactory } from "@multiweb/clients/index";
import { ISectionLoggerFactory, LoggingSection } from "@multiweb/logging";
import { DIContainer } from "Global/Base/DIContainer";
import { GlobalContext } from "Global/Base/GlobalContext";
import { ICommunicationManager } from "OW.Remoting/Remoting/ICommunicationManager";
import { IDataEventsManager } from "OW.Remoting/Remoting/IDataEventsManager";
import { defaultFactory as defaultCommmunicationManagerFactory } from "../../CommunicationManager";
import { defaultFactory as defaultAuthenticationFactory } from "./auth";
import { ClientFactory } from "./ClientFactory";
import { defaultFactory as defaultContextFactory } from "./context";
import { defaultFactory as defaultCultureFactory } from "./culture";
import { defaultFactory as defaultDataEventsFactory } from "./dataEvents";
import { defaultFactory as defaultDataReaderFactory } from "./dataReader";
import { defaultFactory as defaultDocumentsFactory } from "./documents";
import { defaultFactory as defaultExportImportFactory } from "./exportImport";
import { defaultFactory as defaultLocalizationsFactory } from "./localization";
import { defaultFactory as defaultMenuFactory } from "./menu";
import { defaultFactory as defaultModelDescriptionsFactory } from "./modelDescriptions";
import { defaultFactory as defaultNotificationsFactory } from "./notifications";
import { defaultFactory as defaultTasksFactory } from "./tasks";
import { defaultFactory as defaultUserFactory } from "./user";
import { defaultFactory as defaultUserSettings } from "./userSettings";

DIContainer.instance.register(IClientFactory, ClientFactory, true);

/**
 * Zaregistruje výchozí implementace jednotlivých komunikačních klientů.
 * Po této akci už lze získat klienty z _IClientFactory_.
 * @param webServiceUrl Adresa webové služby, se kterou se má komunikovat
 * @param webServiceVersion Verze webové služby, se kterou se má komunikovat
 * @includeToDoc
 */
export function registerDefaultClients(webServiceUrl: string, webServiceVersion?: string) {
    const di = DIContainer.instance;

    di.registerFactory(ICommunicationManager, () => defaultCommmunicationManagerFactory(webServiceUrl, webServiceVersion), true);

    const factory = di.create(IClientFactory);

    const createLogger = (section: LoggingSection) => DIContainer.instance.create(ISectionLoggerFactory).create(section);

    factory.register(ClientType.dataReader, () => defaultDataReaderFactory(di.create(ICommunicationManager), createLogger(LoggingSection.objectWrapper)));
    factory.register(ClientType.menu, () => defaultMenuFactory(di.create(ICommunicationManager), webServiceVersion));
    factory.register(ClientType.user, () => defaultUserFactory(di.create(ICommunicationManager)));
    factory.register(ClientType.notifications, () => defaultNotificationsFactory(factory.get(ClientType.dataReader), di.create(IDataEventsManager)));
    factory.register(ClientType.tasks, () => defaultTasksFactory(di.create(ICommunicationManager)));
    factory.register(ClientType.authentication, () => defaultAuthenticationFactory(di.create(ICommunicationManager), webServiceUrl, webServiceVersion));
    factory.register(ClientType.localizations, () => defaultLocalizationsFactory(webServiceUrl));
    factory.register(ClientType.culture, () => defaultCultureFactory(di.create(ICommunicationManager), webServiceVersion));
    factory.register(ClientType.document, () => defaultDocumentsFactory(di.create(ICommunicationManager)));
    factory.register(ClientType.dataEvents, () => defaultDataEventsFactory(webServiceUrl, webServiceVersion, di.create(ISectionLoggerFactory).create(LoggingSection.webSocket)));
    factory.register(ClientType.userSettings, () => defaultUserSettings(
        di.create(ICommunicationManager),
        factory.get(ClientType.user),
        factory.get(ClientType.dataReader),
        createLogger(LoggingSection.userSettings),
        webServiceVersion));
    factory.register(ClientType.modelDescriptions, () => defaultModelDescriptionsFactory(di.create(ICommunicationManager)));
    factory.register(ClientType.context, () => defaultContextFactory(di.create(ICommunicationManager)));
    factory.register(ClientType.exportImport, () => defaultExportImportFactory(di.create(ICommunicationManager)));
}

GlobalContext.register("getClientFactory", DIContainer.instance.createFactory(IClientFactory));
